import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import TrackingPixel from '../../components/TrackingPixel'
import * as Trackers from '../../components/Trackers'
import ScriptLoader from '../../components/ScriptLoader'
import get from 'lodash.get'
import merge from 'deepmerge'
import { ThemeProvider } from 'pcln-design-system'
import isMobileDevice from '../../utils/isMobileDevice'
import { applicationStateShape } from '../../utils/shapes'
import { getPageType, getType } from '../../utils/getPageType'
import ResponsiveContext from '../../contexts/ResponsiveContext'
import SSOProvider from '../SSOProvider'

export const PartnerContext = React.createContext()
export const PartnerConsumer = PartnerContext.Consumer

const BaseCss = styled('div')`
  ${props => props.override};
  ${props =>
    props.componentStyles.reduce((style, current) => {
      if (current.component.styledComponentId) {
        style += `
          .${current.component.styledComponentId} {
            ${
  typeof current.style === 'function'
    ? current.style(props.theme)
    : current.style
}
          }`
      }

      return style
    }, '')};
`
BaseCss.displayName = 'CssOverride'

const UPDATE_PARTNER_TRACKING_PIXEL = 'UPDATE_PARTNER_TRACKING_PIXEL'
const UPDATE_PARTNER_APPLICATION_STATE = 'UPDATE_PARTNER_APPLICATION_STATE'
const UPDATE_PARTNER_OMITTED = 'UPDATE_PARTNER_OMITTED'

const checkOmit = (omit, component) =>
  (Array.isArray(omit) && omit.includes(component)) || omit === true

const pixelTypes = {
  ...Trackers,
  image: TrackingPixel,
  iframe: TrackingPixel
}

// create wrapper for OneTrust
/* istanbul ignore next */
if (typeof window === 'object') {
  window.OptanonWrapper = function OptanonWrapper () {}
}

/**
 * A provider that can be used to wrap your application and set a partner
 *
 * @param {Object} partner
 * @param {*}      children
 *
 * @returns {null|React.Component}
 */
class PartnerProvider extends React.Component {
  constructor (props) {
    super(props)
    const trackingPixelParameters = get(props, 'trackingPixelParameters', {})
    const applicationState = get(props, 'applicationState', {})
    this.state = {
      omit: {
        trackingPixels:
          checkOmit(props.omit, 'trackingPixels') ||
          !get(props, 'renderTrackingPixel', true),
        headScripts: checkOmit(props.omit, 'headScripts')
      },
      applicationState: merge.all([
        PartnerProvider.getApplicationState(),
        trackingPixelParameters,
        applicationState
      ]),
      prevApplicationProps: merge(trackingPixelParameters, applicationState)
    }
    this.shouldOmit = this.shouldOmit.bind(this)
    this.updateOmit = this.updateOmit.bind(this)
    this.updateTrackingPixel = this.updateTrackingPixel.bind(this)
    this.updateApplicationState = this.updateApplicationState.bind(this)
  }

  static getDerivedStateFromProps (nextProps, prevState) {
    const nextState = {
      applicationState: {
        ...prevState.applicationState,
        random: PartnerProvider.getApplicationState().random,
        pageType: PartnerProvider.getApplicationState().pageType,
        type: PartnerProvider.getApplicationState().type
      },
      prevApplicationProps: {
        ...prevState.prevApplicationProps
      }
    }

    if (typeof nextProps.trackingPixelParameters === 'object') {
      Object.keys(nextProps.trackingPixelParameters).forEach(key => {
        if (
          nextProps.trackingPixelParameters[key] !==
          prevState.prevApplicationProps[key]
        ) {
          nextState.applicationState[key] =
            nextProps.trackingPixelParameters[key]
          nextState.prevApplicationProps[key] =
            nextProps.trackingPixelParameters[key]
        }
      })
    }

    if (typeof nextProps.applicationState === 'object') {
      Object.keys(nextProps.applicationState).forEach(key => {
        if (
          !['random', 'pageType', 'type'].includes(key) &&
          nextProps.applicationState[key] !==
            prevState.prevApplicationProps[key]
        ) {
          nextState.applicationState[key] = nextProps.applicationState[key]
          nextState.prevApplicationProps[key] = nextProps.applicationState[key]
        }
      })
    }

    return nextState
  }

  shouldOmit (component) {
    return this.state.omit[component]
  }

  updateTrackingPixel ({
    renderTrackingPixel = this.shouldOmit('trackingPixels') === false,
    trackingPixelParameters = this.state.applicationState
  }) {
    this.updateApplicationState(trackingPixelParameters)
    this.updateOmit({ trackingPixels: renderTrackingPixel === false })
  }

  static getApplicationState () {
    const pageType = getPageType(get(global, 'window.location.href', ''))
    const navigator = get(global, 'window.navigator', {})
    const deviceType = isMobileDevice(navigator) ? 'mobile' : 'desktop'
    return {
      random: Math.floor(Math.random() * Date.now()) + Date.now(),
      pageType,
      type: getType(pageType),
      lang: (
        (typeof window === 'object' &&
          window.localStorage &&
          window.localStorage.getItem('i18nextLng')) ||
        'en-us'
      ).toLowerCase(),
      amount: null,
      currency: null,
      orderId: null,
      device: deviceType,
      departingDate: null,
      endDate: null,
      origin: null,
      destination: null,
      numAdults: null,
      advancedPurchase: null,
      flyStartDate: null,
      flyEndDate: null,
      originAirportCode: null,
      destinationAirportCode: null,
      value: null
    }
  }

  updateApplicationState (applicationState) {
    this.setState(prevState => ({
      applicationState: {
        ...prevState.applicationState,
        ...applicationState
      }
    }))
  }

  updateOmit (omitted) {
    this.setState(prevState => ({
      omit: {
        ...prevState.omit,
        ...omitted
      }
    }))
  }

  componentDidMount () {
    // Listen for events
    window.addEventListener(
      UPDATE_PARTNER_TRACKING_PIXEL,
      this.updateTrackingPixel
    )
    window.addEventListener(
      UPDATE_PARTNER_APPLICATION_STATE,
      this.updateApplicationState
    )
    window.addEventListener(UPDATE_PARTNER_OMITTED, this.updateOmit)
  }

  componentWillUnmount () {
    // Remove listeners
    window.removeEventListener(
      UPDATE_PARTNER_TRACKING_PIXEL,
      this.updateTrackingPixel
    )
    window.removeEventListener(
      UPDATE_PARTNER_APPLICATION_STATE,
      this.updateApplicationState
    )
    window.removeEventListener(UPDATE_PARTNER_OMITTED, this.updateOmit)
  }

  render () {
    const { appName, partner, componentStyles, options, children } = this.props
    const ssoEnabled = options && options.ssoEnabled
    const { applicationState } = this.state

    if (!children) {
      return null
    }

    if (!partner || !partner.config) {
      return children
    }

    const override =
      partner.config.applyBaseCss &&
      (partner.config.applyBaseCss === true ||
        (appName && partner.config.applyBaseCss.includes(appName)))
        ? require(`../../../config/${partner.plfCode.toLowerCase()}/base`)
          .default
        : ''

    const responsiveProps = {
      smallMinBreakpoint: get(
        partner,
        'config.header.small.minBreakpoint',
        'xs'
      ),
      largeMinBreakpoint: get(
        partner,
        'config.header.large.minBreakpoint',
        'lg'
      )
    }

    const theme = merge(get(partner, 'config.theme', {}), {
      flyQueries: {
        mobile: '@media screen and (max-width: 413px)',
        mobilelg: '@media screen and (min-width: 414px)',
        tablet: '@media screen and (min-width: 600px)',
        tabletlg: '@media screen and (min-width: 768px)',
        tablethd: '@media screen and (min-width: 840px)',
        desktop: '@media screen and (min-width: 1024px)',
        desktoplg: '@media screen and (min-width: 1280px)',
        desktophd: '@media screen and (min-width: 1440px)'
      }
    })

    const oneTrust = get(partner, 'config.oneTrust.domainScript')

    return (
      <PartnerContext.Provider
        value={{
          appName,
          ...partner,
          updateTrackingPixel: this.updateTrackingPixel,
          updateApplicationState: this.updateApplicationState,
          updateOmit: this.updateOmit
        }}
      >
        {!this.shouldOmit('trackingPixels') &&
          get(partner, 'config.trackingPixels', []).map((pixel, idx) => {
            const type = pixel.type
            const Pixel = pixelTypes[type]
            return (
              <Pixel
                key={type + idx}
                env={this.props.env}
                {...pixel.props}
                type={type}
                applicationState={applicationState}
              />
            )
          })}
        {!this.shouldOmit('headScripts') &&
          get(partner, 'config.head.scripts', []).map(script => (
            <ScriptLoader
              key={script}
              url={script}
              applicationState={applicationState}
            />
          ))}
        {!this.shouldOmit('headScripts') &&
          oneTrust && (
          <ScriptLoader
            key='oneTrust'
            url='https://cdn.cookielaw.org/scripttemplates/otSDKStub.js'
            charset='UTF-8'
            data-domain-script={
              this.props.env === 'prod' ? oneTrust.prod : oneTrust.qa
            }
          />
        )}
        <ThemeProvider theme={theme}>
          <BaseCss override={override} componentStyles={componentStyles}>
            <ResponsiveContext.Provider value={responsiveProps}>
              <SSOProvider isEnabled={ssoEnabled}>{children}</SSOProvider>
            </ResponsiveContext.Provider>
          </BaseCss>
        </ThemeProvider>
      </PartnerContext.Provider>
    )
  }
}

PartnerProvider.defaultProps = {
  componentStyles: [],
  omit: false
}

PartnerProvider.propTypes = {
  appName: PropTypes.string,
  partner: PropTypes.shape({
    plfCode: PropTypes.string,
    config: PropTypes.shape({
      trackingPixels: PropTypes.array,
      head: PropTypes.shape({
        scripts: PropTypes.arrayOf(PropTypes.string)
      }),
      theme: PropTypes.object.isRequired
    })
  }),
  renderTrackingPixel: PropTypes.bool,
  trackingPixelParameters: PropTypes.object,
  componentStyles: PropTypes.arrayOf(
    PropTypes.shape({
      component: PropTypes.func.isRequired,
      style: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired
    }).isRequired
  ),
  applicationState: applicationStateShape,
  omit: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.arrayOf(PropTypes.oneOf(['trackingPixels', 'headScripts']))
  ]),
  children: PropTypes.node,
  env: PropTypes.string.isRequired
}

PartnerProvider.displayName = 'PartnerProvider'

export default PartnerProvider
